/** Bird point sprites (flock and the single falcon) and the photo backdrop. */

export const BIRDS_VS = /* glsl */ `#version 300 es
precision highp float;
precision highp int;
precision highp sampler2D;
uniform sampler2D uPos;
uniform sampler2D uVel;
uniform sampler2D uAux;
uniform ivec2 uSimSize;
uniform mat4 uProj;
uniform mat4 uView;
uniform float uPxPerMeterAt1; // viewport height / 2 / tan(fovy / 2)
uniform float uBirdSize;
uniform float uHazeDensity;
uniform float uMaxPoint;
uniform vec3 uCamPos;
uniform vec3 uLightDir;   // world space, from the scene toward the light
// Single-sprite mode (the falcon): state from uniforms instead of textures.
uniform int uSingle;
uniform vec4 uSinglePos;  // xyz, wing phase
uniform vec4 uSingleVel;  // xyz, wing fold 0..1
uniform vec4 uSingleAux;  // roll, size in multiples of a starling, glide, body length factor
out vec2 vBody;
out vec2 vWingL;
out vec2 vWingR;
out float vBodyW;
out float vWingW;
out float vFog;
out float vHalf;
out float vLit;
out float vAgit;

mat3 V3;
float pxm;
vec2 projv(vec3 w) {
  vec3 e = V3 * w;
  return vec2(e.x, -e.y) * pxm; // gl_PointCoord has y down
}

void main() {
  vec4 P; vec4 V; vec4 A;
  if (uSingle == 1) {
    P = uSinglePos; V = uSingleVel; A = uSingleAux;
  } else {
    int id = gl_VertexID;
    ivec2 tc = ivec2(id % uSimSize.x, id / uSimSize.x);
    P = texelFetch(uPos, tc, 0);
    V = texelFetch(uVel, tc, 0);
    A = texelFetch(uAux, tc, 0);
  }
  vec4 viewPos = uView * vec4(P.xyz, 1.0);
  float depth = -viewPos.z;
  if (depth < 1.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    gl_PointSize = 0.0;
    vBody = vWingL = vWingR = vec2(0.0);
    vBodyW = vWingW = vFog = vHalf = vLit = vAgit = 0.0;
    return;
  }
  V3 = mat3(uView);
  pxm = uPxPerMeterAt1 / depth;

  float size = uSingle == 1 ? A.y : uBirdSize * (0.85 + 0.3 * A.y);
  float fold = uSingle == 1 ? V.w : 0.0;
  float bodyMul = uSingle == 1 ? A.w : 1.0;
  if (uSingle == 1) {
    // The falcon is the story: never let it vanish into a 2 px speck.
    float spanPx = 0.38 * size * pxm;
    size *= max(1.0, 12.0 / max(spanPx, 1e-3));
  }
  float span = 0.38 * size;
  float bodyLen = 0.22 * size * bodyMul;
  vec3 dir = normalize(V.xyz + vec3(1e-5, 0.0, 0.0));
  vec3 side = cross(dir, vec3(0.0, 1.0, 0.0));
  float sl = length(side);
  side = sl > 1e-3 ? side / sl : vec3(1.0, 0.0, 0.0);
  vec3 upv = cross(side, dir);
  float roll = A.x;
  float cr = cos(roll), sr = sin(roll);
  vec3 sideR = side * cr + upv * sr;
  vec3 upR = -side * sr + upv * cr;
  float flap = mix(0.8, 0.08, A.z) * sin(P.w);
  float cf = cos(flap), sf = sin(flap);
  // Wings sweep back; a stooping falcon tucks them almost to its body.
  float halfSpan = span * 0.5 * (1.0 - 0.6 * fold);
  vec3 sweep = -dir * span * 0.5 * (uSingle == 1 ? 0.55 + 0.4 * fold : 0.15);
  vec3 wingL = (-sideR * cf + upR * sf) * halfSpan + sweep;
  vec3 wingR = ( sideR * cf + upR * sf) * halfSpan + sweep;
  vec3 body = dir * bodyLen * 0.5;

  vBody = projv(body);
  vWingL = projv(wingL);
  vWingR = projv(wingR);

  // Coverage is what darkens a flock: a wing seen face-on is a 10 cm chord,
  // edge-on a 2 cm line. Width follows how much of the wing plane faces us.
  vec3 toCam = normalize(uCamPos - P.xyz);
  float faceOn = abs(dot(upR, toCam));
  vWingW = size * pxm * mix(0.02, 0.09, faceOn);
  vBodyW = size * pxm * mix(0.055, 0.075, faceOn);
  float ext = max(max(length(vBody), length(vWingL)), length(vWingR)) + max(vBodyW, vWingW) + 1.5;
  float ps = clamp(ext * 2.0, 2.0, uMaxPoint);
  vHalf = ps * 0.5;
  vFog = 1.0 - exp(-depth * uHazeDensity);

  // Lit only where the same wing face sees both the sun and the viewer, so a
  // sun in frame leaves pure silhouettes and side light glints off banking
  // birds. A little rim for backlit translucency on edge-on wings.
  float nl = dot(upR, uLightDir);
  float nv = dot(upR, toCam);
  float lit = max(0.0, nl * nv);
  float rim = 0.1 * max(0.0, -dot(uLightDir, toCam)) * (1.0 - abs(nv));
  vLit = (lit * 0.8 + rim) * (1.0 - vFog);
  vAgit = abs(V.w) * float(1 - uSingle);

  gl_PointSize = ps;
  gl_Position = uProj * viewPos;
}
`;

export const BIRDS_FS = /* glsl */ `#version 300 es
precision highp float;
in vec2 vBody;
in vec2 vWingL;
in vec2 vWingR;
in float vBodyW;
in float vWingW;
in float vFog;
in float vHalf;
in float vLit;
in float vAgit;
uniform vec3 uBirdColor;
uniform vec3 uLitColor;
uniform vec3 uHazeColor;
uniform float uAlpha;
uniform int uDebug;
out vec4 o;

// Capsule centred on the origin, from -b to +b.
float segC(vec2 q, vec2 b, float w) {
  float t = clamp(dot(q, b) / max(dot(b, b), 1e-6), -1.0, 1.0);
  return length(q - b * t) - w;
}
// Tapered capsule from the origin to b.
float segT(vec2 q, vec2 b, float w) {
  float t = clamp(dot(q, b) / max(dot(b, b), 1e-6), 0.0, 1.0);
  return length(q - b * t) - w * (1.0 - 0.55 * t);
}

void main() {
  vec2 q = (gl_PointCoord - 0.5) * (vHalf * 2.0);
  float bw = max(vBodyW, 0.6);
  float ww = max(vWingW, 0.6);
  float dB = segC(q, vBody, bw);
  float dL = segT(q, vWingL, ww);
  float dR = segT(q, vWingR, ww);
  // Sub-pixel widths are drawn at 0.6 px and dimmed, so coverage integrates right.
  float aB = (1.0 - smoothstep(-0.5, 0.5, dB)) * min(1.0, vBodyW / 0.6);
  float aW = max(1.0 - smoothstep(-0.5, 0.5, dL), 1.0 - smoothstep(-0.5, 0.5, dR)) * min(1.0, vWingW / 0.6);
  float a = max(aB, aW);
  if (a < 0.002) discard;
  vec3 col = mix(uBirdColor, uLitColor, min(1.0, vLit));
  if (uDebug == 1) col = mix(vec3(0.1, 0.35, 1.0), vec3(1.0, 0.15, 0.05), vAgit);
  col = mix(col, uHazeColor, vFog);
  o = vec4(col, a * (1.0 - 0.55 * vFog) * uAlpha);
}
`;

export const PHOTO_VS = /* glsl */ `#version 300 es
uniform vec2 uUvScale;
uniform vec2 uUvOffset;
out vec2 vUv;
void main() {
  vec2 v = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  gl_Position = vec4(v * 2.0 - 1.0, 0.0, 1.0);
  vec2 uv = vec2(v.x, 1.0 - v.y);
  vUv = uv * uUvScale + uUvOffset;
}
`;

export const PHOTO_FS = /* glsl */ `#version 300 es
precision mediump float;
in vec2 vUv;
uniform sampler2D uPhoto;
uniform float uAlpha;
out vec4 o;
void main() { o = vec4(texture(uPhoto, vUv).rgb, uAlpha); }
`;
